import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  AlarmClock,
  AlertTriangle,
  CalendarClock,
  CalendarRange,
  CheckCircle2,
  Flame,
  Inbox,
  Loader2,
  Plus,
  RefreshCw,
  Users,
} from 'lucide-react';
import { Button } from '@components/Button';
import { PermissionGuard } from '@components/PermissionGuard';
import { ActivityRow } from '@components/activity/ActivityBits';
import { useAuth } from '@contexts/AuthContext';
import { useProfile } from '@contexts/ProfileContext';
import { useActivityModal } from '@contexts/ActivityModalContext';
import dashboardService from '@services/dashboardService';
import type { Activity } from '@/types/activity';
import type { DashboardScope, DashboardSummary } from '@/types/planning';
import { BOARD_TYPE_LABELS } from '@/types/board';
import { colorClasses } from '@/utils/colors';
import { formatDateBR } from '@/utils/formatters';

interface KpiProps {
  label: string;
  value: number;
  icon: React.ReactNode;
  tone: string;
  hint?: string;
  onClick?: () => void;
}

const Kpi: React.FC<KpiProps> = ({ label, value, icon, tone, hint, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!onClick}
    className="card p-4 text-left hover:shadow-card-hover transition-shadow disabled:cursor-default disabled:hover:shadow-card"
  >
    <div className="flex items-center justify-between">
      <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</span>
      <span className={clsx('p-1.5 rounded-lg', tone)}>{icon}</span>
    </div>
    <p className="text-3xl font-semibold text-slate-900 mt-2">{value}</p>
    {hint && <p className="text-small mt-1">{hint}</p>}
  </button>
);

const ListCard: React.FC<{
  title: string;
  icon: React.ReactNode;
  items: Activity[];
  total: number;
  empty: string;
  onOpen: (a: Activity) => void;
  showAssignee: boolean;
  moreLink?: string;
}> = ({ title, icon, items, total, empty, onOpen, showAssignee, moreLink }) => (
  <div className="card p-4 flex flex-col min-h-[220px]">
    <div className="flex items-center justify-between mb-2">
      <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
        {icon}
        {title}
        <span className="badge-neutral">{total}</span>
      </h3>
      {moreLink && total > items.length && (
        <Link to={moreLink} className="text-xs text-emerald-700 hover:underline">
          Ver todas
        </Link>
      )}
    </div>
    {items.length === 0 ? (
      <p className="text-sm text-slate-400 py-6 text-center flex-1 flex items-center justify-center">{empty}</p>
    ) : (
      <div className="space-y-0.5 max-h-80 overflow-y-auto custom-scrollbar -mx-1">
        {items.map((a) => (
          <ActivityRow key={a.id} activity={a} onClick={() => onOpen(a)} showAssignee={showAssignee} />
        ))}
      </div>
    )}
  </div>
);

/**
 * Painel do dia: o que vence hoje, o que atrasou, o que está sem prazo (pendente de
 * priorização) e os próximos dias. Gestor/admin alterna para a visão da equipe.
 */
export const DashboardView: React.FC = () => {
  const { canManageTeam } = useProfile();
  const { user } = useAuth();
  const firstName = user?.name?.split(' ')[0] ?? '';
  const { openActivity, openNewActivity, version } = useActivityModal();
  const navigate = useNavigate();
  const [scope, setScope] = useState<DashboardScope>('me');
  const [data, setData] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await dashboardService.getSummary(scope));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao carregar o painel');
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    load();
  }, [load, version]);

  const open = (a: Activity) => openActivity(a.id);
  const showAssignee = scope === 'team';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-h2">Olá{firstName ? `, ${firstName}` : ''}!</h1>
          {data && <p className="text-slate-500 mt-1">Hoje é {formatDateBR(data.today)}</p>}
        </div>
        <div className="flex items-center gap-2">
          {canManageTeam && (
            <div className="inline-flex rounded-lg border border-slate-300 bg-white p-0.5">
              {(['me', 'team'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setScope(s)}
                  className={clsx(
                    'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                    scope === s ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:bg-slate-50',
                  )}
                >
                  {s === 'me' ? 'Minhas' : 'Equipe'}
                </button>
              ))}
            </div>
          )}
          <Button variant="outline" size="sm" icon={<RefreshCw />} onClick={load} disabled={loading}>
            Atualizar
          </Button>
          <PermissionGuard permission="activities.write">
            <Button variant="primary" size="sm" icon={<Plus />} onClick={() => openNewActivity()}>
              Nova atividade
            </Button>
          </PermissionGuard>
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
      )}

      {loading && !data ? (
        <div className="flex items-center justify-center py-24 text-slate-400">
          <Loader2 size={20} className="animate-spin mr-2" /> Carregando…
        </div>
      ) : data ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
            <Kpi
              label="Hoje"
              value={data.due_today}
              icon={<AlarmClock size={16} className="text-amber-600" />}
              tone="bg-amber-50"
              hint="vencem hoje"
            />
            <Kpi
              label="Atrasadas"
              value={data.overdue}
              icon={<AlertTriangle size={16} className="text-red-600" />}
              tone="bg-red-50"
              onClick={() => navigate('/activities?overdue=1')}
            />
            <Kpi
              label="Sem prazo"
              value={data.no_due_date}
              icon={<Inbox size={16} className="text-violet-600" />}
              tone="bg-violet-50"
              hint="pendentes de priorização"
              onClick={() => navigate('/activities?no_due_date=1')}
            />
            <Kpi
              label="Críticas"
              value={data.critical}
              icon={<Flame size={16} className="text-rose-600" />}
              tone="bg-rose-50"
              onClick={() => navigate('/activities?priority=critical')}
            />
            <Kpi
              label="Próximos 7 dias"
              value={data.upcoming}
              icon={<CalendarClock size={16} className="text-sky-600" />}
              tone="bg-sky-50"
            />
            <Kpi
              label="Concluídas 7 dias"
              value={data.completed_last_7_days}
              icon={<CheckCircle2 size={16} className="text-emerald-600" />}
              tone="bg-emerald-50"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ListCard
              title="Vencem hoje"
              icon={<AlarmClock size={16} className="text-amber-600" />}
              items={data.due_today_list}
              total={data.due_today}
              empty="Nada vence hoje."
              onOpen={open}
              showAssignee={showAssignee}
            />
            <ListCard
              title="Atrasadas"
              icon={<AlertTriangle size={16} className="text-red-600" />}
              items={data.overdue_list}
              total={data.overdue}
              empty="Nenhuma atividade atrasada. 🎉"
              onOpen={open}
              showAssignee={showAssignee}
              moreLink="/activities?overdue=1"
            />
            <ListCard
              title="Pendentes de priorização"
              icon={<Inbox size={16} className="text-violet-600" />}
              items={data.no_due_date_list}
              total={data.no_due_date}
              empty="Todas as atividades têm prazo."
              onOpen={open}
              showAssignee={showAssignee}
              moreLink="/activities?no_due_date=1"
            />
            <ListCard
              title="Próximos dias"
              icon={<CalendarClock size={16} className="text-sky-600" />}
              items={data.upcoming_list}
              total={data.upcoming}
              empty="Nada previsto para os próximos dias."
              onOpen={open}
              showAssignee={showAssignee}
            />
          </div>

          {scope === 'team' && (
            <div className="card p-4">
              <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2 mb-3">
                <Users size={16} className="text-slate-500" /> Carga da equipe
              </h3>
              {data.by_member.length === 0 ? (
                <p className="text-sm text-slate-400">Nenhum membro no escopo.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                        <th className="py-2 pr-4">Responsável</th>
                        <th className="py-2 px-4 text-right">Abertas</th>
                        <th className="py-2 px-4 text-right">Atrasadas</th>
                        <th className="py-2 px-4 text-right">Vencem hoje</th>
                        <th className="py-2 pl-4 text-right">Sem prazo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.by_member.map((m) => (
                        <tr key={m.team_member_id} className="border-b border-slate-100 last:border-0">
                          <td className="py-2 pr-4">
                            <Link
                              to={`/activities?assignee=${m.team_member_id}`}
                              className="text-slate-800 hover:text-emerald-700 hover:underline"
                            >
                              {m.name}
                            </Link>
                          </td>
                          <td className="py-2 px-4 text-right">{m.open}</td>
                          <td className={clsx('py-2 px-4 text-right', m.overdue > 0 && 'text-red-600 font-semibold')}>
                            {m.overdue}
                          </td>
                          <td className={clsx('py-2 px-4 text-right', m.due_today > 0 && 'text-amber-600 font-medium')}>
                            {m.due_today}
                          </td>
                          <td className={clsx('py-2 pl-4 text-right', m.no_due_date > 0 && 'text-violet-600')}>
                            {m.no_due_date}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {data.boards.length > 0 && (
            <div>
              <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2 mb-3">
                <CalendarRange size={16} className="text-slate-500" /> Eventos e projetos ativos
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {data.boards.map((b) => {
                  const cc = colorClasses(b.color);
                  return (
                    <Link
                      key={b.id}
                      to={`/kanban/${b.id}`}
                      className={clsx('card-hover p-4 border-l-4 block', cc.borderLeft)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-900 truncate">{b.name}</p>
                          <p className="text-small">{BOARD_TYPE_LABELS[b.board_type]}</p>
                        </div>
                        <span className={clsx('w-3 h-3 rounded-full flex-shrink-0 mt-1', cc.swatch)} />
                      </div>
                      {(b.start_date || b.end_date) && (
                        <p className="text-xs text-slate-500 mt-2">
                          {b.start_date ? formatDateBR(b.start_date) : '…'} a {b.end_date ? formatDateBR(b.end_date) : '…'}
                        </p>
                      )}
                      <div className="flex gap-4 mt-3 text-sm">
                        <span className="text-slate-600">
                          <strong className="text-slate-900">{b.open_count}</strong> abertas
                        </span>
                        <span className={clsx(b.overdue_count > 0 ? 'text-red-600' : 'text-slate-400')}>
                          <strong>{b.overdue_count}</strong> atrasadas
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
};
